import api from '@/shared/api/client';

const NOM_DE_SECOURS = 'export.csv';

/**
 * Le nom du fichier tel que le SERVEUR l'annonce, lu dans son en-tête.
 *
 * POURQUOI CETTE FONCTION EXISTE
 *
 * Le téléchargement passe par une URL blob, et une URL blob ne porte aucun en-tête :
 * c'est `a.download` qui décide du nom, pas le serveur. Chaque écran écrivait donc le
 * sien en dur, et le `Content-Disposition` du serveur n'était lu par personne. Le jour
 * où le serveur a mis l'enseigne de l'opérateur dans le nom, l'écran a continué
 * d'enregistrer « clients_flexcore.csv » sans que rien ne le signale.
 *
 * ⚠️ L'ORDRE DES DEUX FORMES N'EST PAS NEUTRE. La RFC 6266 en demande deux :
 * `filename=` translittéré en ASCII pour les vieux clients, et `filename*=UTF-8''`
 * percent-encodé qui porte le nom fidèle. Lire la première rendrait
 * « Eco Flexibilite » là où le serveur envoie « Eco Flexibilité », et on croirait le
 * serveur fautif alors qu'il a tout envoyé.
 */
export function nomDuServeur(entete) {
  if (!entete) return '';
  const fidele = /filename\*=UTF-8''([^;]+)/i.exec(entete);
  if (fidele) {
    try {
      return decodeURIComponent(fidele[1]);
    } catch {
      // Percent-encodage invalide : on retombe sur la forme ASCII plutôt que de rendre
      // une chaîne à moitié décodée.
    }
  }
  const repli = /filename="([^"]+)"/i.exec(entete);
  return repli ? repli[1] : '';
}

/**
 * Télécharge un export et l'enregistre sous le nom que le serveur lui donne.
 *
 * `nomDeSecours` ne sert que si l'en-tête manque — une réponse servie par un proxy qui
 * l'aurait mangée, ou une route qui aurait oublié de le poser.
 */
export async function downloadCsv(endpoint, nomDeSecours = NOM_DE_SECOURS) {
  const res = await api.get(endpoint, { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement('a');
  a.href = url;
  a.download = nomDuServeur(res.headers?.['content-disposition']) || nomDeSecours;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// Le message d'erreur d'une reponse demandee en blob.
//
// Axios applique `responseType: 'blob'` a TOUTE la reponse, erreurs comprises : le
// corps JSON `{"detail": "..."}` arrive donc en Blob, et le `e.response?.data?.detail`
// habituel vaut toujours `undefined`. Sans cette relecture, un ecran de telechargement
// ne peut afficher qu'un message generique, quelle que soit la cause reelle.
export async function messageErreurTelechargement(e, repli) {
  const corps = e?.response?.data;
  if (corps instanceof Blob) {
    try {
      const { detail } = JSON.parse(await corps.text());
      if (detail) return detail;
    } catch {
      // Corps non JSON (une page d'erreur du proxy, une reponse tronquee) : le repli
      // dit au moins que le telechargement a echoue, ce qui reste vrai.
    }
  }
  return corps?.detail || repli;
}
